/**
 * Cumulative net-bb series for the history P/L graph (docs/UI_REFERENCE.md
 * "フィードバック反映"): actual result and all-in-adjusted EV, run as a
 * single running total across the player's *entire* recorded history --
 * winning a match carries its balance forward as the next match's starting
 * point, matches are never independently zero-reset.
 *
 * The graph only *displays* the most recent `windowSize` hands, but the
 * cumulative sum is computed over the full chronological history first and
 * only windowed afterward, so the first plotted point reflects the running
 * balance carried in from hands before the window rather than starting
 * back at 0.
 */

export interface PnlHandLike {
  readonly heroNetChips: number;
  readonly evNetBb?: number;
}

export interface PnlSeries {
  readonly actual: readonly number[];
  readonly ev: readonly number[];
  /** 1-based hand number (across all history) for each plotted point. */
  readonly handNumbers: readonly number[];
}

/**
 * @param chronologicalHands Full history, oldest first, spanning all matches.
 * @param windowSize Number of most-recent hands to keep in the returned series.
 */
export function computeWindowedPnl(
  chronologicalHands: readonly PnlHandLike[],
  windowSize = 100
): PnlSeries {
  const actual: number[] = [];
  const ev: number[] = [];
  let cumActual = 0;
  let cumEv = 0;
  for (const h of chronologicalHands) {
    cumActual += h.heroNetChips / 2;
    cumEv += h.evNetBb ?? h.heroNetChips / 2;
    actual.push(cumActual);
    ev.push(cumEv);
  }

  const total = actual.length;
  const start = Math.max(0, total - windowSize);
  const handNumbers = actual.slice(start).map((_, i) => start + i + 1);

  return { actual: actual.slice(start), ev: ev.slice(start), handNumbers };
}
